import React, { useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";
import FormField from '../../../../../dynamicFormBuilder/FormField';
import { AccountingLayout } from '../Layout';

function AccountingTab(props) {
    var currentUser = props.oidc?.user;
    const DYNAMICFORMSETTING = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const prevManagementSourceRef = useRef(null);

    const loadLookupClient = useCallback((name, dataSource, valueExpr = 'id', displayExpr = 'text') => {
        if (!props.formRefs?.current?.[name]) return;
        const ref = props.formRefs.current[name];
        if (!ref?.instance) return;

        ref.instance().option('dataSource', dataSource);
        ref.instance().option('valueExpr', valueExpr);
        ref.instance().option('displayExpr', displayExpr);

        const currentValue = props.formFields?.[name];
        if (currentValue !== undefined) {
            ref.instance().option('value', currentValue);
        }
    }, [props.formRefs, props.formFields]);

    // Function to enable/disable Department, Fund, and Job fields based on Management Source
    const updateAccountingFieldsState = useCallback((managementSource) => {
        const accountingFields = ['R_GLDEPARTMENT', 'R_FAFUND', 'R_CAJOB'];
        
        if (managementSource === 'C') {
            // Enable fields when Management Source is 'C' (Charge Code)
            accountingFields.forEach(fieldName => {
                if (props.formRefs.current && props.formRefs.current[fieldName]) {
                    const ref = props.formRefs.current[fieldName];
                    if (ref && typeof ref.instance === 'function') {
                        const instance = ref.instance();
                        if (instance && typeof instance.option === 'function') {
                            instance.option('disabled', false);
                        }
                    }
                }
            });
        } else {
            // Disable and clear fields when Management Source is not 'C'
            accountingFields.forEach(fieldName => {
                if (props.formRefs.current && props.formRefs.current[fieldName]) {
                    const ref = props.formRefs.current[fieldName];
                    if (ref && typeof ref.instance === 'function') {
                        const instance = ref.instance();
                        if (instance && typeof instance.option === 'function') {
                            instance.option('disabled', true);
                            instance.option('value', null);
                        }
                    }
                }
            });
            
            // Clear values in formFields state only if they're not already null
            props.setformFields(prevFields => {
                const needsUpdate = prevFields.R_GLDEPARTMENT !== null || 
                                   prevFields.R_FAFUND !== null || 
                                   prevFields.R_CAJOB !== null;
                
                if (needsUpdate) {
                    return {
                        ...prevFields,
                        R_GLDEPARTMENT: null,
                        R_FAFUND: null,
                        R_CAJOB: null
                    };
                }
                return prevFields;
            });
        }
    }, [props.formRefs, props.setformFields]);

    // Custom handleFieldChange that intercepts MANAGEMENTSOURCE changes
    const handleFieldChange = useCallback((name, value) => {
        // Call the parent handleFieldChange first
        props.handleFieldChange(name, value);
        
        // If Management Source changed, update the accounting fields state
        if (name === 'MANAGEMENTSOURCE' && prevManagementSourceRef.current !== value) {
            prevManagementSourceRef.current = value;
            // Use setTimeout to ensure formRefs are ready after state update
            setTimeout(() => {
                updateAccountingFieldsState(value);
            }, 0);
        }
    }, [props.handleFieldChange, updateAccountingFieldsState]);

    const handleAccountingFormLoad = useCallback(() => {
        const managementSources = [
            { id: 'T', text: 'Transaction' },
            { id: 'C', text: 'Charge Code' },
            { id: 'I', text: 'Item' },
            { id: 'U', text: 'Customer' },
        ];

        // Use setTimeout to ensure formRefs are available after FormField renders
        setTimeout(() => {
            loadLookupClient("MANAGEMENTSOURCE", managementSources);
            // Set initial state based on current Management Source value
            const currentMgmtSource = props.formFields?.MANAGEMENTSOURCE;
            if (currentMgmtSource !== undefined) {
                prevManagementSourceRef.current = currentMgmtSource;
                updateAccountingFieldsState(currentMgmtSource);
            }
        }, 100);
    }, [loadLookupClient, props.formFields?.MANAGEMENTSOURCE, updateAccountingFieldsState]);

    // Keep dropdown populated when Management Source changes (but don't call updateAccountingFieldsState here)
    useEffect(() => {
        const managementSources = [
            { id: 'T', text: 'Transaction' },
            { id: 'C', text: 'Charge Code' },
            { id: 'I', text: 'Item' },
            { id: 'U', text: 'Customer' },
        ];

        loadLookupClient("MANAGEMENTSOURCE", managementSources);
    }, [loadLookupClient, props.formFields?.MANAGEMENTSOURCE]);

    return (
        <div>
            <FormField
                currrentUser={currentUser}
                layoutId='5FA92CDE-B3B2-4C36-9966-C5C49E157186'
                layoutName='WMChargeCodeAccountingForm'
                defaultFormSchema={AccountingLayout}
                formFields={props.formFields}
                setformFields={props.setformFields}
                handleFieldValueChange={handleFieldChange}
                DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                formRefs={props.formRefs}
                onFormLoad={handleAccountingFormLoad}
            />
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(AccountingTab);

